import { useState } from 'react'
import { useForwardingDefaults, useUpdateForwardingDefaults } from '../../api/hooks'
import { ApiError } from '../../api/client'
import type { ForwardingDefaults } from '../../api/types'
import { sortCategories } from '../../lib/eventCategories'
import { useAuthStore } from '../../stores/auth'
import { Button, Card, EmptyState, Input, Skeleton } from '../ui'
import { EventCategoryChecklist } from './EventCategoryChecklist'

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** Whole seconds within [min, max], else null. */
function seconds(raw: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(raw.trim())) return null
  const n = Number(raw)
  return n >= min && n <= max ? n : null
}

/**
 * Wisenet WAVE system: forwarding defaults for every camera on it — the
 * event categories cameras inherit unless they pick their own, and the
 * bookmark window around each event. Writes are admin-only.
 */
export function ForwardingDefaultsCard({ systemUid }: { systemUid: string }) {
  const defaults = useForwardingDefaults(systemUid)
  return (
    <Card title="Event forwarding">
      {defaults.isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-12" />
          <Skeleton className="h-16" />
        </div>
      ) : defaults.isError ? (
        <EmptyState title="Could not load forwarding defaults" hint={errDetail(defaults.error)} />
      ) : (
        <ForwardingDefaultsForm systemUid={systemUid} defaults={defaults.data} />
      )}
    </Card>
  )
}

function ForwardingDefaultsForm({ systemUid, defaults }: { systemUid: string; defaults: ForwardingDefaults }) {
  const isAdmin = useAuthStore((s) => s.customer?.role === 'admin')
  const update = useUpdateForwardingDefaults(systemUid)

  const [categories, setCategories] = useState<string[]>(sortCategories(defaults.categories))
  const [pre, setPre] = useState(String(defaults.pre_seconds))
  const [post, setPost] = useState(String(defaults.post_seconds))

  const readOnly = !isAdmin
  const preSeconds = seconds(pre, 0, 60)
  const postSeconds = seconds(post, 1, 300)
  const valid = preSeconds !== null && postSeconds !== null

  function save() {
    if (preSeconds === null || postSeconds === null) return
    update.mutate({ categories, pre_seconds: preSeconds, post_seconds: postSeconds })
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
        Defaults for every camera on this system. Cameras set to inherit get these categories; each
        bookmark spans the event plus the seconds before and after.
      </p>

      <div className="flex flex-col gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          Default categories
        </span>
        <EventCategoryChecklist value={categories} onChange={setCategories} disabled={readOnly} />
        {categories.length === 0 && (
          <p className="text-[11px] text-ostrel-warn">
            No categories — cameras that inherit will not bookmark any events.
          </p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Seconds before"
          type="number"
          inputMode="numeric"
          min={0}
          max={60}
          step={1}
          value={pre}
          disabled={readOnly}
          onChange={(e) => setPre(e.target.value)}
          error={preSeconds === null ? 'Whole seconds, 0–60' : undefined}
          className="tnum"
        />
        <Input
          label="Seconds after"
          type="number"
          inputMode="numeric"
          min={1}
          max={300}
          step={1}
          value={post}
          disabled={readOnly}
          onChange={(e) => setPost(e.target.value)}
          error={postSeconds === null ? 'Whole seconds, 1–300' : undefined}
          className="tnum"
        />
      </div>

      {update.isError && <p className="text-xs text-ostrel-danger">{errDetail(update.error)}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="navy"
          size="sm"
          disabled={readOnly || update.isPending || !valid}
          title={readOnly ? 'Requires the admin role' : undefined}
          onClick={save}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        {update.isSuccess && !update.isPending && <span className="text-[11px] text-ostrel-ok">Saved.</span>}
      </div>
    </div>
  )
}
